// frontend/src/utils/exportReportExcel.ts
//
// Excel de los reports de cliente. Sigue el patrón del repo: `buildXWorkbook`
// puro y testeable, más un wrapper que descarga.
//
// Se usa ExcelJS y no SheetJS porque aquí hacen falta a la vez tres cosas que
// solo ExcelJS da: estilo por fila (gris y cursiva en las clases especiales),
// formato numérico de verdad y fechas como fecha en lugar de texto. La jerarquía
// se marca por dos vías —`outlineLevel` para poder plegar los grupos en Excel y
// la indentación visual— para que se lea igual con los grupos abiertos.
//
// Las cabeceras van en inglés, con el texto exacto del informe que ya recibe el
// cliente, y NO se traducen: el objetivo es que nadie tenga que traducir entre
// los dos documentos.
import ExcelJS from 'exceljs'
import type { TFunction } from 'i18next'
import type { AnyReportRow } from '@/types'
import type { ReportBlock } from '@/utils/reportBlocks'
import { campo, claseEspecial } from '@/components/reports/formato'
import { DECIMALES_CANTIDAD } from '@/components/reports/formato'
import { colAppliesTo, isNumericCol, sectionLabel, type ReportCol, type ReportDef } from '@/components/reports/reportDefs'

// Paleta compartida con exportSecuenciaExcel / exportProduccionDiariaExcel.
const HEADER = { bg: 'FF0284C7', font: 'FFFFFFFF' }   // sky-600
const BAND = 'FFF8FAFC'                                // slate-50
const BORDER = 'FFE2E8F0'                              // slate-200
const SECTION = { bg: 'FFE0F2FE', font: 'FF0369A1' }  // sky-100 / sky-700
const ESPECIAL_FONT = 'FF94A3B8'                       // slate-400
const LIMITANTE = { bg: 'FFFEF3C7', font: 'FF92400E' } // amber-100 / amber-800

function fill(argb: string): ExcelJS.Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } }
}

function thinBorder() {
  const side = { style: 'thin' as const, color: { argb: BORDER } }
  return { top: side, left: side, bottom: side, right: side }
}

/** Formato numérico con separador de miles y los decimales que pida la columna. */
function numFmtFor(col: ReportCol): string {
  // Tres decimales por defecto: la materia prima se compra en kilos y litros, y
  // ahí el decimal ES el dato. `#,##0.000` los muestra siempre, que en una hoja de
  // cálculo es lo correcto — la columna se lee en vertical y alineada.
  const d = col.decimals ?? DECIMALES_CANTIDAD
  if (col.kind === 'pct') return d > 0 ? `#,##0.${'0'.repeat(d)}"%"` : '#,##0"%"'
  return d > 0 ? `#,##0.${'0'.repeat(d)}` : '#,##0'
}

/**
 * Escribe el valor de una celda con su tipo real.
 *
 * Tres reglas:
 * - Ausente → celda **vacía**, nunca 0. En estos reports null significa «no
 *   aplica», y un 0 se leería como «no queda nada».
 * - Fecha → `Date` con `numFmt`, para que Excel la ordene y la filtre como fecha
 *   en lugar de como texto.
 * - Número → número con separador de miles, no una cadena ya formateada.
 */
function writeCell(cell: ExcelJS.Cell, row: AnyReportRow, col: ReportCol, isHeader: boolean): void {
  if (!colAppliesTo(col, isHeader)) return          // no aplica a este nivel
  const raw = campo(row, col.key)
  if (raw == null || raw === '') return             // no aplica / sin dato

  // Marca: se escribe su texto solo en la fila que lo es, y nada en las demás. Un
  // aviso que aparece en todas las filas deja de avisar, y volcar el 1/0 crudo no
  // dice nada a quien lee el fichero.
  if (col.kind === 'flag') {
    if (Number(raw) === col.flagOn) cell.value = col.flagText ?? 'YES'
    return
  }

  // Recuento condicional: solo a partir del mínimo declarado.
  if (col.kind === 'count') {
    const n = Number(raw)
    if (!Number.isFinite(n) || n < (col.minToShow ?? 1)) return
    cell.value = n
    cell.numFmt = numFmtFor(col)
    return
  }

  if (col.kind === 'date') {
    const iso = String(raw).slice(0, 10)
    const [y, m, d] = iso.split('-').map(Number)
    if (!y || !m || !d) { cell.value = String(raw); return }
    // Mediodía UTC para que ningún desplazamiento de zona mueva el día.
    cell.value = new Date(Date.UTC(y, m - 1, d, 12))
    cell.numFmt = 'dd/mm/yyyy'
    return
  }

  if (isNumericCol(col)) {
    const n = Number(raw)
    if (!Number.isFinite(n)) { cell.value = String(raw); return }
    cell.value = n
    cell.numFmt = numFmtFor(col)
    return
  }

  const texto = String(raw)
  // Mismo mapa que la pantalla: el fichero y la tabla no pueden decir cosas
  // distintas del mismo valor.
  cell.value = col.valueLabels?.[texto] ?? texto
}

// ── Construcción del workbook (pura, testeable) ───────────────────────────────

/** Sello legible, con hora: el dato cambia dentro del mismo día. */
function fmtSello(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

export function buildReportWorkbook(
  def: ReportDef,
  blocks: ReportBlock<AnyReportRow>[],
  cols: ReportCol[],
  t: TFunction,
  generadoEn: Date = new Date(),
): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'Airval Factory'

  const ws = wb.addWorksheet(t(`${def.titleKey}`).slice(0, 31) || def.key)
  ws.columns = cols.map(c => ({ width: c.excelWidth ?? 16 }))

  // ── Marca de tiempo ────────────────────────────────────────────────────────
  //
  // El dato se mueve todos los días según avanza la fábrica. El cliente guardará
  // este fichero y lo abrirá dentro de semanas: sin fecha no sabrá qué está
  // mirando, y las diferencias contra su propio sistema parecerán errores nuestros.
  const sello = ws.addRow([])
  sello.getCell(1).value = `${def.excelBase}  ·  generated ${fmtSello(generadoEn)}`
  ws.mergeCells(sello.number, 1, sello.number, cols.length)
  sello.getCell(1).font = { italic: true, size: 9, color: { argb: 'FF64748B' } }

  // ── Fila de cabeceras ──────────────────────────────────────────────────────
  const head = ws.addRow(cols.map(c => c.label))
  head.height = 26
  for (let i = 1; i <= cols.length; i++) {
    const cell = head.getCell(i)
    cell.font = { bold: true, color: { argb: HEADER.font }, size: 10 }
    cell.fill = fill(HEADER.bg)
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
    cell.border = thinBorder()
  }
  // Se congelan las dos primeras filas: el sello y las cabeceras.
  ws.views = [{ state: 'frozen', ySplit: head.number }]
  ws.autoFilter = {
    from: { row: head.number, column: 1 },
    to: { row: head.number, column: cols.length },
  }

  let seccionActual: unknown

  for (const block of blocks) {
    // Banda de sección (producto terminado: TERMINADO / SEMIELABORADO).
    const seccion = campo(block.header, 'seccion')
    if (seccion != null && seccion !== seccionActual) {
      seccionActual = seccion
      const banda = ws.addRow([])
      banda.getCell(1).value = sectionLabel(def, seccion)
      ws.mergeCells(banda.number, 1, banda.number, cols.length)
      banda.getCell(1).font = { bold: true, color: { argb: SECTION.font }, size: 10 }
      banda.getCell(1).fill = fill(SECTION.bg)
    }

    // ── Cabecera del bloque ──────────────────────────────────────────────────
    const hRow = ws.addRow([])
    cols.forEach((col, i) => {
      const cell = hRow.getCell(i + 1)
      writeCell(cell, block.header, col, true)
      cell.font = { bold: true, size: 10 }
      cell.fill = fill(BAND)
      cell.border = thinBorder()
      if (isNumericCol(col)) cell.alignment = { horizontal: 'right' }
    })

    // ── Sublíneas ────────────────────────────────────────────────────────────
    for (const detail of block.details) {
      const dRow = ws.addRow([])
      // Agrupable en Excel: el usuario puede plegar el detalle igual que en la
      // aplicación.
      dRow.outlineLevel = 1

      const especial = claseEspecial(detail)
      const limitante = Number(campo(detail, 'esComponenteLimitante') ?? 0) === 1

      cols.forEach((col, i) => {
        const cell = dRow.getCell(i + 1)
        writeCell(cell, detail, col, false)
        cell.border = thinBorder()
        cell.font = {
          size: 10,
          // Gris y cursiva en las clases especiales, igual que el Excel que ya
          // se entrega: sin lote, lote sin pedido y lote no atribuido.
          italic: !!especial,
          color: { argb: especial ? ESPECIAL_FONT : 'FF334155' },
        }
        if (isNumericCol(col)) cell.alignment = { horizontal: 'right' }
        // La indentación mantiene visible la jerarquía con los grupos abiertos.
        if (i === 0) cell.alignment = { ...(cell.alignment ?? {}), indent: 1 }
        if (limitante) cell.fill = fill(LIMITANTE.bg)
      })
    }
  }

  // Grupos plegados desde el botón de Excel, no plegados por defecto: el informe
  // se lee entero de arriba abajo.
  ws.properties.outlineLevelRow = 1

  return wb
}

/** Construye y descarga el Excel del report. */
export async function exportReportExcel(
  def: ReportDef,
  blocks: ReportBlock<AnyReportRow>[],
  cols: ReportCol[],
  t: TFunction,
  today: Date = new Date(),
): Promise<void> {
  const wb = buildReportWorkbook(def, blocks, cols, t, today)
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${def.excelBase}_${today.toISOString().slice(0, 10)}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}
